import { useEffect, useMemo, useState } from "react";
import { useProfile } from "context/ProfileContext";
import { supabase } from "services/supabaseClient";

function toOwner(profile) {
  if (!profile?.id) return null;
  return {
    id: profile.id,
    firstName: String(profile.first_name || "").trim(),
    lastName: String(profile.last_name || "").trim(),
  };
}

export default function useCurrentOwner(createdBy) {
  const { profile } = useProfile();
  const signedInOwner = useMemo(() => toOwner(profile), [profile]);
  const ownerId = createdBy || profile?.id || null;
  const [currentOwner, setCurrentOwner] = useState(() =>
    ownerId && ownerId === profile?.id ? signedInOwner : null
  );

  useEffect(() => {
    let cancelled = false;

    if (!ownerId) {
      setCurrentOwner(null);
      return () => {
        cancelled = true;
      };
    }

    if (ownerId === profile?.id) {
      setCurrentOwner(signedInOwner);
      return () => {
        cancelled = true;
      };
    }

    setCurrentOwner((existing) =>
      existing?.id === ownerId ? existing : { id: ownerId, firstName: "", lastName: "" }
    );

    supabase
      .from("profiles")
      .select("id, first_name, last_name")
      .eq("id", ownerId)
      .maybeSingle()
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data) {
          setCurrentOwner({ id: ownerId, firstName: "", lastName: "" });
          return;
        }
        setCurrentOwner(toOwner(data));
      });

    return () => {
      cancelled = true;
    };
  }, [ownerId, profile?.id, signedInOwner]);

  return currentOwner;
}
